import { sql } from 'drizzle-orm'
import { index, integer, sqliteTable, text, uniqueIndex } from 'drizzle-orm/sqlite-core'

import { id, timestamps } from './_columns'
import { sites } from './sites'

/**
 * A client's own domain for a site (issue #83). The domain is a zone in our Cloudflare account; the client points its
 * nameservers at Cloudflare, and once the zone is active the console attaches the hostnames to the site's Workers.
 * A site may have several (as many as its package's `domains` limit); a domain belongs to one site. The primary one
 * carries the CMS and is the site's address; the others redirect to it.
 */
export const siteDomains = sqliteTable(
  'site_domains',
  {
    id: id(),
    siteId: text('site_id')
      .notNull()
      .references(() => sites.id, { onDelete: 'cascade' }),
    /** The registrable (apex) domain, lowercase, e.g. `example.co.id`. */
    domain: text('domain').notNull(),
    /** Hostnames the public site answers on: the apex and `www.` by default. */
    webHostnames: text('web_hostnames', { mode: 'json' }).$type<string[]>().notNull(),
    /** Hostname of the CMS, `cms.<domain>` by default. */
    adminHostname: text('admin_hostname').notNull(),
    /** Cloudflare zone id. */
    zoneId: text('zone_id').notNull(),
    /** `pending_ns` (waiting for the client's nameservers) → `active` (zone live) → `attached` (site answers on it), or `error`. */
    status: text('status').$type<'pending_ns' | 'active' | 'attached' | 'error'>().notNull().default('pending_ns'),
    /** The two nameservers Cloudflare assigned, to send to the client. */
    nameServers: text('name_servers', { mode: 'json' }).$type<string[]>().notNull().default([]),
    lastCheckedAt: text('last_checked_at'),
    /** Last failure (attach, zone moved…), shown as is. */
    error: text('error'),
    attachedAt: text('attached_at'),
    /**
     * The site's main domain: its web and CMS origins, and where the others redirect. At most one per site (index
     * below); the first domain connected becomes it, and while a site has a connected domain, its primary is connected.
     */
    isPrimary: integer('is_primary', { mode: 'boolean' }).notNull().default(false),
    /**
     * The site's mail domain: the mailbox takes mail for it (besides the platform address). At most one per site, set
     * apart from the primary: switching the primary never moves mail. Set when the first domain is connected on a
     * package with the mailbox; changed only by staff (POST /api/sites/:id/mail-domain).
     */
    isMail: integer('is_mail', { mode: 'boolean' }).notNull().default(false),
    ...timestamps,
  },
  (t) => [
    index('ix_site_domains_site').on(t.siteId),
    uniqueIndex('ux_site_domains_primary')
      .on(t.siteId)
      .where(sql`${t.isPrimary} = 1`),
    uniqueIndex('ux_site_domains_mail')
      .on(t.siteId)
      .where(sql`${t.isMail} = 1`),
    uniqueIndex('ux_site_domains_domain').on(t.domain),
    index('ix_site_domains_status').on(t.status),
  ],
)

export type SiteDomainRow = typeof siteDomains.$inferSelect
